import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type AuthContextJSON = z.infer<typeof AuthContextSchema.AuthContextJSONZ>;
export type AuthContextParams = z.infer<typeof AuthContextSchema.AuthContextZ>;

export default class AuthContextSchema {
  public static readonly AuthContextJSONZ = z.object({
    viewingAsVendor: z.boolean().nullish(),
  });

  public static readonly AuthContextZ = z.object({
    viewingAsVendor: z.boolean().optional(),
  });

  public static validate(values: unknown): AuthContextParams {
    return parseOrThrow(AuthContextSchema.AuthContextZ, values, {
      logError: true,
      schemaName: "AuthContextSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): AuthContextJSON {
    return parseOrThrow(AuthContextSchema.AuthContextJSONZ, json, {
      logError: true,
      schemaName: "AuthContextSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is AuthContextParams {
    try {
      AuthContextSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AuthContextJSON {
    try {
      AuthContextSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AuthContextJSON): AuthContextParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(AuthContextSchema.AuthContextJSONZ, json, {
      logError: true,
      schemaName: "AuthContextSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return AuthContextSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: AuthContextParams): AuthContextJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AuthContextSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AuthContextSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(AuthContextSchema.AuthContextJSONZ, AuthContextSchema.AuthContextZ, {
    decode: (data) => ({
      viewingAsVendor: data.viewingAsVendor ?? undefined,
    }),
    encode: (context) => ({
      viewingAsVendor: context.viewingAsVendor,
    }),
  });
}
